import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from 'src/app/core/services/notification.service';
import { FrequentlyAskedQuestionsAPIService } from 'src/app/extensions/services/api/faq-api.service';
import { CanFAQDeactivate } from 'src/app/core/guards/faq-manager.guard';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { PopupComponent } from 'src/app/extensions/components/popup/popup.component';
import { DeleteItemModalComponent } from 'src/app/extensions/components/delete-item-modal/delete-item-modal.component';

@Component({
  selector: 'admin-faq-list',
  templateUrl: './faq-list.component.html',
  styleUrls: ['./faq-list.component.scss'],
  standalone: false,
})
export class FrequentlyAskedQuestionsListComponent implements OnInit, CanFAQDeactivate {
  public hasNoData = true;
  public frequentlyAskedQuestions: app.models.FAQModel[];
  public faqInitial: app.models.FAQModel[];
  public props: app.models.DynamicPropsDictionary;
  public lastQuestionOrder: number = 0;
  public isOrderUnchanged = true;
  public isFiltered = false;

  constructor(
    private _faqAPIService: FrequentlyAskedQuestionsAPIService,
    private _router: Router,
    private _ngbModal: NgbModal,
    private _translateService: TranslateService,
    private _notificationService: NotificationService,
    private _globalService: GlobalService
  ) {}

  async canDeactivate(): Promise<boolean> {
    if (!this.isOrderUnchanged) {
      const modal = this._ngbModal.open(PopupComponent, {
        backdrop: 'static',
      });
      modal.componentInstance.title = 'common.confirmDataLossTitle';
      modal.componentInstance.description = 'common.confirmDataLoss';
      const modalResult = await modal.result;
      if (!modalResult) {
        this._globalService.pathChanged = this._router.url.split('/')[1];
      }
      return modalResult;
    }
    return true;
  }

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.isOrderUnchanged = true;
    this._faqAPIService.getFilteredFrequentlyAskedQuestions('').subscribe(result => {
      this.frequentlyAskedQuestions = result.data;
      this.faqInitial = [...this.frequentlyAskedQuestions];
      this.props = result.props;
      this.hasNoData = this.frequentlyAskedQuestions.length == 0;
      if (!this.hasNoData) {
        this.lastQuestionOrder = this.faqInitial.length;
      }
    });
  }

  filterQuestionsList(questionsFilter: string) {
    this.isOrderUnchanged = true;
    if (questionsFilter.length == 0 || questionsFilter.length > 2) {
      this._faqAPIService.getFilteredFrequentlyAskedQuestions(questionsFilter).subscribe(result => {
        this.frequentlyAskedQuestions = result.data;
        this.props = result.props;
        this.hasNoData = this.frequentlyAskedQuestions.length == 0;
      });
      this.isFiltered = questionsFilter.length != 0;
    }
  }

  createFAQ() {
    this._router.navigate(['faq-manager/new']);
  }

  editDetailsHandler = (frequentlyAskedQuestionId: string) => {
    this._router.navigate(['faq-manager', frequentlyAskedQuestionId]);
  };

  deleteFrequentlyAskedQuestionHandler = (frequentlyAskedQuestionId: string) => {
    const deleteFAQModal = this._ngbModal.open(DeleteItemModalComponent, {
      backdrop: 'static',
    });

    deleteFAQModal.componentInstance.title = 'faqManager.deleteFaqTitle';
    deleteFAQModal.componentInstance.warninglabel = this._translateService.instant('faqManager.deleteFaqWarning');
    deleteFAQModal.componentInstance.item = this.frequentlyAskedQuestions.find(
      x => x.frequentlyAskedQuestionId == frequentlyAskedQuestionId
    ).question;
    deleteFAQModal.result.then(result => {
      if (result) {
        this._faqAPIService.deleteFrequentlyAskedQuestion(frequentlyAskedQuestionId).subscribe(response => {
          if (response.isSuccess) {
            this.frequentlyAskedQuestions = this.frequentlyAskedQuestions.filter(
              x => x.frequentlyAskedQuestionId != frequentlyAskedQuestionId
            );
            this.faqInitial = this.frequentlyAskedQuestions.filter(
              x => x.frequentlyAskedQuestionId != frequentlyAskedQuestionId
            );
            this._notificationService.showSuccess(
              this._translateService.instant('faqManager.notifications.faqDeleted'),
              this._translateService.instant('common.success')
            );
          } else {
            this._notificationService.showError(
              this._translateService.instant('faqManager.notifications.faqDeleteFailed'),
              this._translateService.instant('common.error')
            );
          }
        });
      }
    });
  };

  saveFrequentlyAskedQuestionsOrder() {
    this._faqAPIService.saveFrequentlyAskedQuestionsOrder(this.frequentlyAskedQuestions).subscribe(response => {
      if (response.isSuccess) {
        this._notificationService.showSuccess(
          this._translateService.instant('faqManager.notifications.faqsOrderSaved'),
          this._translateService.instant('common.success')
        );
        this.faqInitial = [...this.frequentlyAskedQuestions];
        this.isOrderUnchanged = true;
      } else {
        this._notificationService.showError(
          this._translateService.instant('faqManager.notifications.faqsOrderSaveFailed'),
          this._translateService.instant('common.error')
        );
      }
    });
  }

  checkOrder() {
    this.isOrderUnchanged = true;
    this.frequentlyAskedQuestions.forEach(x => {
      if (this.faqInitial.indexOf(x) !== this.frequentlyAskedQuestions.indexOf(x)) {
        this.isOrderUnchanged = false;
        return;
      }
    });
  }
}
